import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchContent } from '../api/api';
import './BikeToWork.css';

const DEFAULTS = {
  heroTitle: 'Bike to Work Scheme',
  heroSubtitle: 'Save up to 52% on your new electric bike through the Government Tax Incentive Scheme',
  heroCta: 'Shop Electric Bikes',
  heroCtaLink: '/products?category=electric-bikes',
  introTitle: 'What is the Bike to Work Scheme?',
  introText: 'The Bike to Work Scheme is a Government tax incentive scheme that allows employees to purchase a new bicycle and safety equipment tax free. You can save between 31% and 52% depending on your tax rate. The scheme is available to PAYE workers and the self-employed.',
  limitEmployee: '€1,500',
  limitCargo: '€3,000',
  steps: [
    { icon: '1', title: 'Choose Your Bike', text: 'Browse our range of electric bikes and select the one you want.' },
    { icon: '2', title: 'Apply with Employer', text: 'Ask your employer or HR department to register with the scheme.' },
    { icon: '3', title: 'Get Your Voucher', text: 'Receive your tax-free voucher or purchase order from your employer.' },
    { icon: '4', title: 'Collect In Store', text: 'Bring your voucher to our store and ride away on your new bike!' },
  ],
  savingsTitle: 'How Much Can You Save?',
  savingsRows: [
    { label: 'Bike Price', standard: '€1,500', higher: '€1,500' },
    { label: 'Tax Relief (20% / 40%)', standard: '€300', higher: '€600' },
    { label: 'PRSI Relief (4%)', standard: '€60', higher: '€60' },
    { label: 'USC Relief (8%)', standard: '€120', higher: '€120' },
    { label: 'You Pay', standard: '€1,020', higher: '€720' },
    { label: 'Total Saving', standard: '€480 (32%)', higher: '€780 (52%)' },
  ],
  faqTitle: 'Frequently Asked Questions',
  faqs: [
    { q: 'Who is eligible?', a: 'Any PAYE employee or self-employed person who pays tax in Ireland is eligible for the scheme.' },
    { q: 'How often can I use the scheme?', a: 'You can use the scheme once every 4 years.' },
    { q: 'Can I use the scheme for an electric bike?', a: 'Yes! Electric bikes are fully covered under the scheme up to €1,500.' },
    { q: 'What if my bike costs more than the limit?', a: 'You can pay the difference yourself. The tax relief applies to the first €1,500 (or €3,000 for cargo bikes).' },
    { q: 'Do I need to use the bike for commuting?', a: 'Yes, the bike must be used mainly for commuting to and from work.' },
  ],
  ctaTitle: 'Ready to Get Started?',
  ctaText: 'Visit our store or browse online. Our team will help you choose the perfect electric bike and guide you through the scheme.',
  ctaBtn: 'Shop Electric Bikes',
  ctaBtnLink: '/products?category=electric-bikes',
  ctaPhone: '087 100 2241',
};

function BikeToWork() {
  const [data, setData] = useState(DEFAULTS);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchContent('bike-to-work')
      .then(d => setData({ ...DEFAULTS, ...d }))
      .catch(() => setData(DEFAULTS));
  }, []);

  const steps = Array.isArray(data.steps) ? data.steps : DEFAULTS.steps;
  const faqs = Array.isArray(data.faqs) ? data.faqs : DEFAULTS.faqs;
  const savingsRows = Array.isArray(data.savingsRows) ? data.savingsRows : DEFAULTS.savingsRows;

  return (
    <div className="btw-page">

      {/* Hero */}
      <div className="btw-hero">
        <div className="container">
          <div className="btw-hero-badge">Government Tax Incentive</div>
          <h1>{data.heroTitle}</h1>
          <p>{data.heroSubtitle}</p>
          <Link to={data.heroCtaLink || '/products?category=electric-bikes'} className="btn btw-hero-btn">
            {data.heroCta}
          </Link>
        </div>
      </div>

      {/* Intro */}
      <section className="btw-section">
        <div className="container btw-intro-grid">
          <div className="btw-intro-text">
            <h2>{data.introTitle}</h2>
            <p>{data.introText}</p>
            <div className="btw-limits">
              <div className="btw-limit-card">
                <span className="btw-limit-amount">{data.limitEmployee}</span>
                <span className="btw-limit-label">Standard Bike Limit</span>
              </div>
              <div className="btw-limit-card btw-limit-card--green">
                <span className="btw-limit-amount">{data.limitCargo}</span>
                <span className="btw-limit-label">Cargo / E-Bike Limit</span>
              </div>
            </div>
          </div>
          <div className="btw-intro-badge">
            <div className="btw-save-circle">
              <span className="btw-save-pct">52%</span>
              <span className="btw-save-label">Max Saving</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="btw-section btw-section--grey">
        <div className="container">
          <h2 className="btw-section-title">How It Works</h2>
          <div className="btw-steps">
            {steps.map((step, i) => (
              <div key={i} className="btw-step">
                <div className="btw-step-icon">{step.icon || i + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings table */}
      <section className="btw-section">
        <div className="container">
          <h2 className="btw-section-title">{data.savingsTitle}</h2>
          <div className="btw-table-wrap">
            <table className="btw-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Standard Rate (20%)</th>
                  <th>Higher Rate (40%)</th>
                </tr>
              </thead>
              <tbody>
                {savingsRows.map((row, i) => (
                  <tr key={i} className={i === savingsRows.length - 1 ? 'btw-table-highlight' : ''}>
                    <td>{row.label}</td>
                    <td>{row.standard}</td>
                    <td>{row.higher}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="btw-section btw-section--grey">
        <div className="container btw-faq-wrap">
          <h2 className="btw-section-title">{data.faqTitle}</h2>
          <div className="btw-faqs">
            {faqs.map((faq, i) => (
              <div key={i} className={`btw-faq${openFaq === i ? ' open' : ''}`}>
                <button className="btw-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <span className="btw-faq-arrow">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="btw-faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="btw-cta">
        <div className="container">
          <h2>{data.ctaTitle}</h2>
          <p>{data.ctaText}</p>
          <div className="btw-cta-actions">
            <Link to={data.ctaBtnLink || '/products?category=electric-bikes'} className="btn btw-cta-btn">
              {data.ctaBtn}
            </Link>
            {data.ctaPhone && (
              <a href={`tel:${data.ctaPhone.replace(/\s/g, '')}`} className="btw-cta-phone">
                Call us: {data.ctaPhone}
              </a>
            )}
          </div>
        </div>
      </section>

    </div>
  );
}

export default BikeToWork;
